import { useEffect, useRef, useState } from 'react';
import '../../styles/onboardingFlow/login/login.scss';
import { useNavigate } from 'react-router-dom';
import { consumePendingLoginError } from './pendingLoginError';
import store from '../../store/store';
import {
  setAccessToken,
  setAgencyAccessToken,
  setEmployeeIdentity,
  setHideWebAuthnPrompt,
  setIsLoggedInViaWebAuthn,
  setIsWebAuthnAvailableInAccount,
  setIsWebAuthnRegistrationSkipped,
} from '../../store/reducers/authSlice';
import { useTranslation } from 'react-i18next';
import { TWO_FACTOR_AUTHENTICATION_LOGIN_ENDPOINT } from '../../lib/constants/urls';
import { RequestInfo } from '../../interfaces/RequestInfo';
import { asyncHandler } from '../../lib/api/async-handler';
import { fetchAppInit } from '../../lib/api/app-init';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import { setIsInitSuccess, setMessage } from '../../store/reducers/appSlice';
import {
  applicationJSONBodyBuilder,
  applicationWWWXRLEncodedBodyBuilder,
} from '../../lib/utils/body-builder';
import moment from 'moment';
import { RootState } from '../../store/store';
import { useSelector } from 'react-redux';
import SlideFromBottom from '../../ui/sliders/SlideFromBottom';
import { OTPCodeInput } from '../../ui/inputs/OTPCodeInput';
import {
  APP_ENV,
  LOGIN_API,
  USER_INFO_API,
  WEBAUTHN_LOGIN_API,
} from '../../lib/constants/central-api';
import { submitLogToBugsnag } from '../../lib/api/log';
import {
  employeeLogin,
  fetchIdentity,
} from '../../lib/api/identity/identity-api';
import FooterLinks from '../../components/footer/FooterLinks';
import { debounce } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import useTitle from '../../customHooks/useTitle';
import AINormalInput from '../../ui/inputs/AINormalInput';
import googleButtonImg from '../../assets/googleIcon.png';
import passkeyButtonImg from '../../assets/passkey.png';
import { useGoogleLogin } from '@react-oauth/google';
import { applyThemeToApp } from '../../lib/helper-functions/themeUtils';
import { setAppThemeMode, setUserSettings } from '../../store/reducers/uiSlice';
import { setIsOnboardingMetricsActive } from '../../store/reducers/userSlice';
import adupLogoSet from '../../assets/onboarding/common/adup-logo-set.png';
import MobileLogoCard from '../mobileLogoCard/MobileLogoCard';

declare const window: any;

const Login = () => {
  const { t } = useTranslation();
  useTitle(t('SignIn'));
  const params = new URLSearchParams(window.location.search);

  const [loading, setLoading] = useState(false);
  const emailInputRef = useRef(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [password, setPassword] = useState('');
  // The reason the last sign-in attempt failed, shown under the heading. Kept
  // in page state so a global toast reset cannot clear it.
  const [loginError, setLoginError] = useState('');
  // A sign-in failure handed over by the portal login or a failed app init,
  // read once from the one-shot session marker. Navigation state is not used:
  // it lives in the browser history and would survive a reload.
  const [handedOverError] = useState<string>(() => consumePendingLoginError());
  const [rememberFlag, setRememberFlag] = useState<boolean>(false);
  const [isSliderOpen, setIsSliderOpen] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isWebAuthnButtonVisible, setIsWebAuthnButtonVisible] =
    useState<boolean>(false);
  const authToken = store.getState().auth.authorization.accessToken;
  const is_ai_onboarding_inprogress = useSelector(
    (state: RootState) => state.user?.ai_onboarding?.is_ai_onboarding_inprogress
  );

  const [secretKey2fa, setSecretKey2fa] = useState('');
  const [userId2fa, setUseId2fa] = useState('');

  const navigate = useNavigate();

  const isDarkMode = useSelector((state: RootState) => state.UI.theme.dark);

  /**
   * Show why sign-in failed: inline on the page and as a toast. Dispatched on
   * every call, so a repeated attempt with the same reason is reported again.
   * Falls back to a generic message when the server gave no reason.
   */
  function showLoginError(message?: unknown) {
    const reason =
      typeof message === 'string' && isNotEmptyString(message.trim())
        ? message
        : t('LoginFailed-msg');
    setLoginError(reason);
    store.dispatch(
      setMessage({
        message: reason,
        messageType: 'error',
      })
    );
  }

  useEffect(() => {
    const input: any = emailInputRef.current;

    const checkAutofill = () => {
      if (input && isNotEmptyString(input.value)) {
        getUserDetails(input.value);
      }
    };

    const observer = new IntersectionObserver(checkAutofill);
    observer.observe(input);

    return () => {
      if (observer && input) {
        observer.unobserve(input);
      }
    };
  }, []);

  async function triggerWebAuthn(email: string) {
    setIsDisabled(true);
    try {
      const loginUUID = uuidv4();

      const assertOptionsConfig = {
        path: `${WEBAUTHN_LOGIN_API}/options`,
        body: {
          key: loginUUID,
          email: email,
        },
      };

      const assertConfig = {
        path: LOGIN_API,
        body: {
          webauthn: true,
          key: loginUUID,
        },
      };

      try {
        console.log('WebAuthn : Authenticating...');

        const webpass = window.Webpass.create({
          rpId: APP_ENV === 'LIVE' ? 'adup.io' : 'kodeia.com',
        });

        const { user: response, success }: any = await webpass.assert(
          assertOptionsConfig,
          assertConfig
        );

        if (success) {
          normalLoginProcess(response, true);
          console.log('WebAuthn : Login successful!');
        } else {
          console.log('WebAuthn : Login failed.', response);
          setIsDisabled(false);
        }
      } catch (error) {
        console.log('WebAuthn : Login error.', error);
        submitLogToBugsnag(
          'error',
          `Error in webauthn Login endpoint: ${error}`
        );
        setIsDisabled(false);
      }
    } catch (error) {
      console.log('WebAuthn : Login error.', error);
      submitLogToBugsnag('error', `Error in webauthn Login endpoint: ${error}`);
      setIsDisabled(false);
    }
    setIsDisabled(false);
  }

  const normalLoginProcess = (response: any, isWebAuthnLogin?: boolean) => {
    store.dispatch(setIsInitSuccess(false));
    const token_type = 'Bearer';
    const { access_token, user, agency_api_key } = response?.data;
    const hideWebAuthnPrompt = user?.user_settings?.hide_webauthn_prompt;

    if (isNotEmptyString(access_token)) {
      store.dispatch(
        setAccessToken({
          sellerToken: access_token,
          type: token_type,
          expiresOn:
            rememberFlag === true
              ? moment().add(21, 'days')
              : moment().add(1, 'days'),
        })
      );

      // Phase 11-Bridge: store the agency-portal employee key minted alongside
      // the JWT so the new portal can authenticate immediately. Null/missing
      // when the user is not a non-white-label seller (e.g. admin) — in that
      // case we leave the agency slice empty and fall back to legacy redirect.
      if (isNotEmptyString(agency_api_key)) {
        store.dispatch(setAgencyAccessToken(agency_api_key));
      }

      store.dispatch(setIsWebAuthnAvailableInAccount(user?.webauthn ?? false));
      store.dispatch(setIsLoggedInViaWebAuthn(isWebAuthnLogin ?? false));
      store.dispatch(setHideWebAuthnPrompt(hideWebAuthnPrompt ?? 0));
      if (hideWebAuthnPrompt === 1) {
        store.dispatch(setIsWebAuthnRegistrationSkipped(true));
      }

      if (user?.user_settings) {
        store.dispatch(setUserSettings(user?.user_settings));

        applyThemeToApp(
          user?.user_settings?.theme_mode as any,
          isDarkMode,
          () => store.dispatch(setAppThemeMode())
        );
      }

      store.dispatch(
        setIsOnboardingMetricsActive(
          user?.is_onboarding_metrics_active === true
        )
      );

      setLoading(false);
      if (is_ai_onboarding_inprogress) {
        navigate('/onboarding');
      } else if (isNotEmptyString(agency_api_key)) {
        // Phase 11-Bridge / Phase 12: successful seller login mints an emp_ key
        // and lands on the dashboard (legacy FE, now wired with new proposal +
        // stats cards). Admin / non-seller logins fall back to the legacy home.
        navigate('/dashboard');
      } else {
        navigate('/');
      }

      //make init call to get all info about logged-In user
      fetchAppInit();
    } else {
      setLoading(false);
      console.log('Access Token is empty');
      submitLogToBugsnag('error', `Access Token is empty`);
      setIsDisabled(false);
      showLoginError(t('UnauthorizedAccess'));
    }
  };

  /**
   * Sign-in for an INVITED agency employee.
   *
   * `LOGIN_API` authenticates the legacy seller `users` table, which an invited
   * employee has no row in — so the email + password they chose when accepting
   * their invite came back "Invalid credentials" no matter what they typed.
   * This retries the same credentials against the employee endpoint, which
   * authenticates `agency_employees` and returns an `emp_` key plus the httpOnly
   * session cookie that keeps them signed in across reloads.
   *
   * There is no seller JWT here — an employee has no seller account — so the
   * legacy seller-JWT surfaces stay unavailable, exactly as they always were for
   * this kind of user.
   *
   * @returns true when the employee sign-in succeeded and navigation happened.
   */
  async function employeeLoginProcess(): Promise<boolean> {
    try {
      const res = await employeeLogin(emailAddress, password);

      if (!isNotEmptyString(res?.agency_api_key)) {
        return false;
      }

      store.dispatch(setIsInitSuccess(false));
      store.dispatch(setAgencyAccessToken(res.agency_api_key));

      // Seed the identity from the login response, then upgrade to the full
      // /me card (accessible brands, account type) — mirrors the invite-accept
      // handler in SignUp.tsx. A failed upgrade is non-fatal: App.tsx retries
      // /me off the agency token.
      if (res.employee) {
        store.dispatch(
          setEmployeeIdentity({
            type: 'employee',
            employee_id: res.employee.id ?? null,
            email: res.employee.email ?? null,
            name: res.employee.name ?? null,
            role: res.employee.role ?? null,
          })
        );
      }

      try {
        store.dispatch(
          setEmployeeIdentity(await fetchIdentity(res.agency_api_key))
        );
      } catch (identityErr) {
        submitLogToBugsnag(
          'warning',
          `[Login employee] fetchIdentity after login failed: ${identityErr}`
        );
      }

      setLoading(false);
      setIsDisabled(false);
      navigate('/dashboard');
      return true;
    } catch (err) {
      // Not an employee either, or wrong password — let the caller surface the
      // original seller-login error rather than a second, confusing one.
      return false;
    }
  }

  /**
   * @param { string } email The corresponding email of the user
   */
  async function getUserDetails(email: string) {
    const accessToken = store.getState().auth.authorization.accessToken;
    const isWhiteLabel = store.getState().UI?.isWhiteLabel;
    let sellerToken: string;
    if (isWhiteLabel) {
      sellerToken = accessToken?.currentWhiteLabelSellerToken;
    } else {
      sellerToken = accessToken?.sellerToken;
    }

    setEmailAddress(email);
    setIsWebAuthnButtonVisible(false);

    const emailPattern = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/;

    if (
      emailPattern.test(email) &&
      window.Webpass.isSupported() &&
      !isNotEmptyString(sellerToken)
    ) {
      const requestInfo: RequestInfo = {
        url: `${USER_INFO_API}`,
        method: 'POST',
        body: JSON.stringify({
          email: email,
        }),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json',
        },
      };

      const actionType = 'GET_USER_DETAILS';
      const customMsg = 'User Details fetched Successfully.';

      const res: any = await asyncHandler(
        requestInfo,
        actionType,
        customMsg,
        false
      );

      const response = res?.data;

      if (
        res &&
        res?.actionType === actionType &&
        response &&
        response?.success === true
      ) {
        if (response?.data && response?.data?.webauthn) {
          setIsWebAuthnButtonVisible(true);
        }
        return response?.data;
      } else {
        return false;
      }
    }
    return false;
  }

  const emailChangeDebounce = debounce((email) => getUserDetails(email), 600);

  async function handleClick() {
    setLoading(true);
    setLoginError('');
    try {
      const requestInfo: RequestInfo = {
        url: LOGIN_API,
        method: 'POST',
        body: applicationJSONBodyBuilder({
          email: emailAddress,
          password,
          remember_me: rememberFlag ?? false,
        }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        // Store the httpOnly `tara_session` cookie the API sets on login so the
        // session is restored on reload (the boot `/me` call rides this cookie).
        credentials: 'include',
      };

      const actionType = 'LOGIN_VERIFICATION';
      const customMsg = t('LoginSuccess-msg');

      const res: any = await asyncHandler(
        requestInfo,
        actionType,
        customMsg,
        false,
        true
      );

      // asyncHandler resolves to null on a network error or an unreadable body.
      const response = res?.data;

      if (res?.actionType === actionType && response?.success === true) {
        if (response?.result?.secret_key) {
          //TODO 2FA Auth - Central API not supported yet
          setSecretKey2fa(response?.result?.secret_key);
          setUseId2fa(response?.result?.user_id);
          setIsSliderOpen(true);
        } else {
          normalLoginProcess(response);
        }
      } else if (await employeeLoginProcess()) {
        // Handled by the employee fallback — it navigates on success.
      } else {
        showLoginError(response?.message);
        setLoading(false);
        setIsDisabled(false);
      }
    } catch (exception) {
      setLoading(false);
      setIsDisabled(false);
      showLoginError();
      console.log('Error during authentication request');
      submitLogToBugsnag('error', `${exception}`);
    }
  }

  /* =============2FA Auth stuff start ================ */
  async function OTPSubmitHandler(otpCode: string) {
    try {
      const sendingData = {
        otp_code: otpCode,
        Tfa_secret: secretKey2fa,
        user_id: userId2fa,
      };

      const requestInfo: RequestInfo = {
        url: TWO_FACTOR_AUTHENTICATION_LOGIN_ENDPOINT,
        method: 'POST',
        body: applicationWWWXRLEncodedBodyBuilder(sendingData),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
        },
        // Store the httpOnly `tara_session` cookie for reload restore.
        credentials: 'include',
      };

      const actionType = 'SENDING-2FA-OTP';
      const customMsg = t('2fa-VerifiedSuccess-msg');

      const res: any = await asyncHandler(requestInfo, actionType, customMsg);

      const response = res.data;

      if (res.actionType === actionType && response.success) {
        normalLoginProcess(response);
        setIsSliderOpen(false);
      }
    } catch (exception) {
      console.log('Error during authentication request');
      submitLogToBugsnag('error', `${exception}`);
    }
  }
  /* =============2FA Auth stuff end ================ */

  /* ============= Google OAuth Sign In start ================ */
  const googleSignIn = async (googleAccessToken: any) => {
    try {
      const sendingData = {
        oauth: 'google',
        role: 'seller',
        token: googleAccessToken,
        bypass: params.get('bypass') === 'true',
      };

      const requestInfo: RequestInfo = {
        url: LOGIN_API,
        method: 'POST',
        body: applicationWWWXRLEncodedBodyBuilder(sendingData),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
        },
        // Store the httpOnly `tara_session` cookie for reload restore.
        credentials: 'include',
      };

      const actionType = 'LOGIN_VERIFICATION';
      const customMsg = t('LoginSuccess-msg');

      const res: any = await asyncHandler(
        requestInfo,
        actionType,
        customMsg,
        false,
        true
      );

      const response = res?.data;

      if (res?.actionType === actionType && response?.success === true) {
        normalLoginProcess(response);
      } else {
        showLoginError(response?.message);
        setLoading(false);
      }
    } catch (exception) {
      setLoading(false);
      console.log('Error during authentication request');
      submitLogToBugsnag('error', `${exception}`);
    }
  };

  const googleAuthentication = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      googleSignIn(tokenResponse?.access_token);
    },
  });
  /* ============= Google OAuth Sign In end ================ */

  const onFormSubmit = (e: any) => {
    e.preventDefault();

    if (isNotEmptyString(emailAddress) && isNotEmptyString(password)) {
      setIsDisabled(true);
      setTimeout(() => {
        handleClick();
      }, 700);
    }
  };

  if (!authToken.sellerToken) {
    return (
      <div className='onboarding-login'>
        <div className='onboarding-login__container'>
          {/* Mobile logo card (slideshow, mobile only) */}
          <MobileLogoCard />

          {/* Logo (desktop only; hidden on mobile when card is shown) */}
          <div className='onboarding-login__logo'>
            <img src={adupLogoSet} alt='AdUp Logo' />
          </div>

          {/* Heading */}
          <div className='onboarding-login__header'>
            <h1>{t('SignIn')}</h1>
            <p>{t('SignInSubText-new-ui')}</p>
          </div>

          {loginError && (
            <p
              role='alert'
              className='onboarding-login__submit-error'
              style={{
                color: '#d32f2f',
                textAlign: 'center',
                margin: '0 0 1rem',
              }}
            >
              {loginError}
            </p>
          )}

          {!loginError && handedOverError && (
            <p
              role='alert'
              className='onboarding-login__handover-error'
              style={{
                color: '#d32f2f',
                textAlign: 'center',
                margin: '0 0 1rem',
              }}
            >
              {handedOverError}
            </p>
          )}

          {/* Form */}
          <form onSubmit={onFormSubmit} className='onboarding-login__form'>
            {/* Email Input */}
            <div className='onboarding-login__field'>
              <AINormalInput
                id='email'
                ref={emailInputRef}
                value={emailAddress}
                type='email'
                changeListeners={[
                  (e) => {
                    setIsWebAuthnButtonVisible(false);
                    emailChangeDebounce(e.target.value);
                  },
                ]}
                placeholder={t('EmailAddress')}
                innerPlaceholder={'youremail@example.com'}
                required={true}
                pattern='^(([^<>\(\)\[\]\\.,;:\s@"]+(\.[^<>\(\)\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$'
                autoFocus={true}
              />
            </div>

            {/* Password Input */}
            <div className='onboarding-login__field'>
              <AINormalInput
                id='password'
                value={password}
                changeListeners={[(e) => setPassword(e.target.value)]}
                placeholder={t('Password')}
                type='password'
                innerPlaceholder={t('EnterYourPassword')}
                required={true}
              />
            </div>

            {/* Remember Me and Forgot Password */}
            <div className='onboarding-login__options'>
              <label className='onboarding-login__checkbox-label'>
                <input
                  type='checkbox'
                  className='onboarding-login__checkbox-input'
                  checked={rememberFlag}
                  onChange={(e) => setRememberFlag(e.target.checked)}
                />
                <span className='onboarding-login__checkbox-custom'></span>
                <span className='onboarding-login__checkbox-text'>
                  {t('RememberMe')}
                </span>
              </label>
              <span
                className='onboarding-login__forgot-password'
                onClick={() => navigate('/forgot-password')}
              >
                {t('ForgotPassword')}
              </span>
            </div>

            {/* Continue Button */}
            <div className='onboarding-login__button'>
              <MainSquareButton
                value={t('Continue')}
                disabled={isDisabled}
                type='submit'
                classes='onboarding-login__continue-btn'
                loading={loading}
              />
            </div>

            {/* OR Separator */}
            <div className='onboarding-login__separator'>
              <span>{t('OR')}</span>
            </div>

            {/* Google Button */}
            <div className='onboarding-login__button'>
              <MainSquareButton
                value={t('ContinueWithGoogle')}
                disabled={isDisabled}
                image={googleButtonImg}
                type='button'
                onClick={() => {
                  googleAuthentication();
                }}
                classes='onboarding-login__google-btn'
              />
            </div>

            {/* Passkey Button (if visible) */}
            {isWebAuthnButtonVisible && (
              <div className='onboarding-login__button'>
                <MainSquareButton
                  value={t('SignInWithPasskey')}
                  disabled={isDisabled}
                  image={passkeyButtonImg}
                  type='button'
                  onClick={() => {
                    triggerWebAuthn(emailAddress);
                  }}
                  classes='onboarding-login__passkey-btn'
                />
              </div>
            )}

            {/* Sign Up Link */}
            <div className='onboarding-login__signup'>
              <span>
                {t('DontHaveAnAccount')}{' '}
                <span
                  className='onboarding-login__signup-link'
                  onClick={() => navigate('/register')}
                >
                  {t('SignUpLink')}
                </span>
              </span>
            </div>

            {/* Terms and Privacy */}
            <div className='onboarding-login__terms'>
              <span>{t('ByContinuingYouAgree')} </span>
              <FooterLinks />
            </div>
          </form>
        </div>

        {/* Copyright */}
        <div className='onboarding-login__copyright'>
          <span>© AdUp {new Date().getFullYear()}</span>
        </div>

        {/* 2FA OTP Modal */}
        {isSliderOpen && (
          <SlideFromBottom
            hideSegment={() => {
              setIsSliderOpen(false);
            }}
          >
            <div className='onboarding-login__otp'>
              <h1>{t('EnterOTP')}</h1>
              <OTPCodeInput
                numberOfFields={6}
                onComplete={(code: string) => {
                  OTPSubmitHandler(code);
                }}
              />
            </div>
          </SlideFromBottom>
        )}
      </div>
    );
  } else {
    window.location.assign('/');
    return null;
  }
};

export default Login;
